import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import {
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaWallet,
  FaClock,
  FaShieldAlt,
} from "react-icons/fa";

const ConfirmWithdrawal = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [withdrawal, setWithdrawal] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    // We don't verify the token on load, just show the confirmation UI
    // Token will be verified when user clicks confirm
    setLoading(false);
  }, [token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const response = await api.get(`/withdrawals/confirm/${token}`);

      if (response.data.success) {
        setConfirmed(true);
        setWithdrawal(response.data.withdrawal);

        // Redirect to dashboard after 5 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 5000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to confirm withdrawal. The link may be invalid or expired.",
      );
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
          <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-4xl text-emerald-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-3">
              Withdrawal Confirmed!
            </h1>

            <p className="text-slate-300 mb-6">
              Your withdrawal has been confirmed successfully and will be
              processed by our team shortly.
            </p>

            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6">
              <div className="space-y-2 text-sm">
                {withdrawal && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Amount:</span>
                      <span className="text-white font-semibold">
                        ${withdrawal.netAmount?.toFixed(2)} USDT
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Status:</span>
                      <span className="text-emerald-300 font-semibold">
                        Confirmed
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="text-sm bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 mb-6 text-cyan-200">
              <strong>⏱️ Processing Time:</strong> 2-24 hours
              <br />
              You'll receive an email once completed
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className="btn-primary w-full"
            >
              Go to Dashboard
            </button>

            <p className="text-xs text-slate-500 mt-4">
              Redirecting in 5 seconds...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaExclamationTriangle className="text-4xl text-rose-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-3">
              Confirmation Failed
            </h1>

            <p className="text-slate-300 mb-6">{error}</p>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-6">
              <p className="text-xs text-amber-200">
                <strong>Common reasons:</strong>
                <br />• Link has expired (30 min limit)
                <br />• Already confirmed
                <br />• Invalid confirmation link
              </p>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className="btn-secondary w-full"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Initial confirmation prompt
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaWallet className="text-4xl text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Confirm Your Withdrawal
            </h1>
            <p className="text-slate-400 text-sm">
              Please review and confirm your withdrawal request
            </p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <FaShieldAlt className="text-amber-400" />
                <h3 className="text-white font-semibold text-sm">
                  Security Notice
                </h3>
              </div>
              <ul className="text-xs text-slate-300 space-y-1.5">
                <li>• This confirmation link expires in 30 minutes</li>
                <li>• Once confirmed, withdrawal cannot be cancelled</li>
                <li>• Processing time: 2-24 hours</li>
              </ul>
            </div>

            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <FaClock className="text-cyan-400" />
                <h3 className="text-white font-semibold text-sm">
                  What happens next?
                </h3>
              </div>
              <p className="text-xs text-slate-300">
                After confirmation, your withdrawal will be queued for
                processing by our admin team. You'll receive an email once the
                transaction is completed.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirming ?
                <div className="flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin" />
                  Confirming...
                </div>
              : "✓ Confirm Withdrawal"}
            </button>

            <button
              onClick={() => navigate("/dashboard")}
              disabled={confirming}
              className="w-full btn-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">
              If you didn't request this withdrawal, please secure your account
              immediately
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmWithdrawal;
